const getEscapedPipesRemoved = (str: string) => {
  return str?.replace(/\\\|/g, '');
};

const addAlignmentToLegacyTables = (markdown: string): string => {
  const splitByRowDelimiter = markdown
    // The following line fixes an issue caused by a temporary bug
    // in the Rich Text Editor. The bug resulted in invalid markdown
    // by escaping the newline character in table headers. This line
    // replaces the invalid markdown with the correct markdown. While
    // a database migration could fix the invalid markdown, it would
    // be complex. Additionally, we are not yet certain if this is the
    // only issue the bug created.
    .replace(/\|\\n\|/g, '|\n|')
    // Split the markdown by table rows (|\n|) to find tables
    .split(/\|\n\|/g);
  const processedSections: string[] = [];
  let tableRows: string[] = [];
  let inTable = false;

  splitByRowDelimiter
    .filter(section => !!section)
    .forEach((section, index) => {
      const cleanedSection = getEscapedPipesRemoved(section);

      // If the current section contains unescaped pipes (part of a table)
      // OR if we're already in a table (for single-column tables where middle rows have no pipes)
      if (cleanedSection.includes('|') || inTable) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }

        // Split by "|\n" to handle both the end of the current table and the start of the next table
        const parts = section.split('|\n');

        parts.forEach((part, partIndex) => {
          if (part) {
            if (inTable) {
              tableRows = [...tableRows, part];
            }
            const isTableEnd =
              (partIndex === 0 && parts.length > 1) ||
              (part.endsWith('|') && index === splitByRowDelimiter.length - 1);
            if (isTableEnd) {
              // Process and push the current table
              const formattedTable = processTable(tableRows);
              processedSections.push(formattedTable);

              // Reset for the next table
              tableRows = [];
              inTable = false;
            }

            // This block should only run if we're transitioning from one table to another in the same section
            if (!isTableEnd && partIndex > 0 && tableRows.length === 0) {
              // Start the new table
              tableRows = [part];
              inTable = true;
            }
          }
        });
      } else {
        // Process the table that just ended
        if (inTable) {
          const formattedTable = processTable(tableRows);
          processedSections.push(formattedTable);
          tableRows = [];
          inTable = false;
        }
        // Push non-table content
        processedSections.push(section);
      }
    });

  // Handle case where markdown ends with a table
  if (inTable && tableRows.length && !!tableRows[0]) {
    const formattedTable = getEscapedPipesRemoved(tableRows[0]).includes('|')
      ? processTable(tableRows)
      : tableRows[0];
    processedSections.push(formattedTable);
  }

  // Rebuild the markdown
  return processedSections.join('|\n');
};

// Split and process the table content by rows and add alignment if necessary
const processTable = (tableRows: string[]): string => {
  if (!tableRows.length) {
    return '';
  }
  const columnCount = countColumns(tableRows[0]);

  // Check if there's already an alignment row and add one if missing
  const hasAlignmentRow = tableRows.some(row =>
    row
      .split('|')
      .filter(cell => cell.length)
      .map(cell => cell.trim())
      .every(cell => /^:?-{3,}:?$/.test(cell))
  );
  if (!hasAlignmentRow) {
    const alignmentRow = `${new Array(columnCount).fill(' --- ').join('|')}`;
    const newTableRows = [tableRows[0], alignmentRow, ...tableRows.slice(1)];
    return newTableRows.join('|\n|');
  }

  return tableRows.join('|\n|');
};

// Helper function to count the number of columns in a row
const countColumns = (row: string): number => {
  // Split the row, ignoring escaped pipes, and count unescaped pipes
  return getEscapedPipesRemoved(row).split('|').length - 1;
};

export { addAlignmentToLegacyTables };
